import { FallbackErrorBoundary } from "@/components/FallbackErrorBoundary";
import { ItemCard } from "@/components/ItemCard";
import { ListContainer } from "@/components/layout/ListContainer";
import { useModals } from "@/features/modals/hooks";
import { useActiveProject } from "@/features/project/ActiveProject";
import { setActiveProject } from "@/features/project/setActiveProject";
import { currencyFormatter } from "@/utils/currencyFormatter";
import {
  type Opportunity,
  type QuotationInfo,
  deleteQuotation,
} from "@repo/api";
import { Text, crossIcon, pencilIcon, shoppingBagIcon } from "@repo/skapa";
import { getLanguageLocal, t } from "@repo/translation";
import { useSettings, useToast } from "@repo/ui";
import { type Toast, getErrorMessage, trackAnalytics } from "@repo/utils";

import { QuotationCardDetails } from "./QuotationCardDetails";

const onDeleteQuotation = ({
  opportunityId,
  quotationId,
  updateToast,
}: {
  opportunityId?: number;
  quotationId: string;
  updateToast: (toast: Toast) => void;
}) =>
  deleteQuotation(opportunityId, quotationId)
    .then(() => {
      updateToast({
        id: quotationId,
        title: t("prompt.quotation_removed"),
      });
    })
    .catch((e) => {
      updateToast({
        id: quotationId,
        title: t("prompt.failed_to_remove_quotation"),
        message: getErrorMessage(e),
        type: "error",
      });
    });

interface QuotationItemProps {
  opportunity?: Opportunity;
  currency?: string;
  quotation: QuotationInfo;
  editable?: boolean;
  viewThisQuotation: () => void;
}
const QuotationItem = ({
  opportunity,
  currency,
  quotation,
  editable,
  viewThisQuotation,
}: QuotationItemProps) => {
  const { pushModal } = useModals();
  const { updateToast } = useToast();
  const { market, language, general } = useSettings();

  const onRemoveFromProject = () =>
    pushModal({
      type: "CONFIRM",

      onConfirm: () =>
        onDeleteQuotation({
          opportunityId: opportunity?.id,
          quotationId: quotation.id,
          updateToast,
        }),
      title: t("action.remove_from_project"),
      message: t("prompt.confirm_remove_quotation"),
    });

  const openQuotationInSalja = {
    onAddToBag: (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      if (opportunity) {
        setActiveProject(opportunity);
      }
      const link = `${
        import.meta.env.VITE__WEB_SALJA_V2
      }/${market}/${language}/orders/view/${"quote"}/${quotation.id}`;

      window.open(link, "_blank");
    },
    icon: shoppingBagIcon,
    disabled: false,
    loading: false,
  };

  const viewAttachments = () => {
    pushModal({
      type: "QUOTATION",
      quotationId: quotation.id,
      activeTab: "order-attachments",
    });
  };

  const menuItems = [
    {
      icon: pencilIcon,
      title: t("action.view_attachments"),
      onClick: () => {
        viewAttachments();
        trackAnalytics("open_order_attachments");
      },
    },
    {
      icon: crossIcon,
      title: t("action.remove_from_project"),
      onClick: () => {
        onRemoveFromProject();
        trackAnalytics("remove_quotation_from_project");
      },
    },
  ];

  return (
    <ItemCard
      editable={editable}
      menuItems={menuItems}
      onClick={viewThisQuotation}
      image={quotation.imageUrl}
      amount={currencyFormatter({
        value: quotation.amount ?? 0,
        currency,
        locale: getLanguageLocal(language, market),
        showSign: true,
        disableDecimals: Boolean(general?.localization?.disable_decimals),
        fractions: 2,
      })}
      addedToProjectValue={quotation.isIncludedInOppValue}
      actions={[
        {
          id: `add-to-cart-button__quotation-${quotation.id}`,
          text: `${t("action.open_order_in_salja")} - ${quotation.title}`,
          title: t("action.open_order_in_salja"),
          icon: openQuotationInSalja.icon,
          iconOnly: true,
          disabled: openQuotationInSalja.disabled,
          loading: openQuotationInSalja.loading,
          onClick: openQuotationInSalja.onAddToBag,
          type: "emphasised",
        },
      ]}
      details={<QuotationCardDetails quotation={quotation} />}
    />
  );
};

interface QuotationProps {
  editable?: boolean;
}
export const Quotations = ({ editable }: QuotationProps) => {
  const { opportunity } = useActiveProject();
  const { pushModal } = useModals();
  const quotations =
    opportunity?.quotations?.quotationInfo?.sort(
      (a, b) =>
        new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime()
    ) ?? [];

  return (
    <div>
      {quotations.length === 0 && <Text>{t("prompt.no_quotations")}</Text>}
      {quotations.length > 0 && (
        <ListContainer>
          {quotations.map((quotation) => (
            <FallbackErrorBoundary
              errorMessage={`Failed to render quote: ${quotation?.id}`}
              key={quotation.id}
            >
              <QuotationItem
                opportunity={opportunity}
                currency={opportunity?.currency}
                quotation={quotation}
                editable={editable}
                viewThisQuotation={() => {
                  if (editable) {
                    pushModal({ type: "QUOTATION", quotationId: quotation.id });
                  }
                }}
              />
            </FallbackErrorBoundary>
          ))}
        </ListContainer>
      )}
    </div>
  );
};
